import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDashboardVisibility, DashboardType } from '@/hooks/useDashboardVisibility';
import { useSalesUsers } from '@/hooks/useSalesUsers';
import { useAppUsers } from '@/hooks/useAppUsers';
import { Loader2, Plus, Trash2, Monitor, Search, Users } from 'lucide-react';

export const DashboardManagement: React.FC = () => {
  const [selectedDashboard, setSelectedDashboard] = useState<DashboardType>('sdr');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  const { visibilityList, isLoading, addUser, removeUser, toggleVisibility, isAdding, isRemoving } = 
    useDashboardVisibility(selectedDashboard);
  
  const { salesUsers } = useSalesUsers();
  const { users: allUsers } = useAppUsers();

  const getDashboardTitle = (type: DashboardType) => {
    return type === 'sdr' ? 'Dashboard SDR' : 'Dashboard Consultants';
  };

  const filteredVisibility = visibilityList?.filter(v => 
    v.user?.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const availableUsers = allUsers.filter(user => 
    !visibilityList?.some(v => v.user_id === user.id) && user.active
  );

  const handleAddUser = () => {
    if (selectedUserId) {
      addUser({ userId: selectedUserId, dashboardType: selectedDashboard });
      setSelectedUserId('');
      setShowAddUser(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Dashboard Management</h2>
        <p className="text-muted-foreground">
          Control which users appear on TV dashboards
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Dashboard Visibility</CardTitle>
              <CardDescription>
                Manage which users appear on each dashboard
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Monitor className="w-5 h-5 text-muted-foreground" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Dashboard Selector */}
            <div className="flex items-center gap-4">
              <Label htmlFor="dashboard-select" className="min-w-[100px]">Dashboard:</Label>
              <Select value={selectedDashboard} onValueChange={(v) => setSelectedDashboard(v as DashboardType)}>
                <SelectTrigger id="dashboard-select" className="w-[250px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sdr">SDR Dashboard</SelectItem>
                  <SelectItem value="consultant">Consultants Dashboard</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="secondary" className="ml-auto">
                <Users className="w-3 h-3 mr-1" />
                {visibilityList?.filter(v => v.is_visible).length || 0} visible
              </Badge>
            </div>

            {/* Search Bar */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button onClick={() => setShowAddUser(!showAddUser)} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </div>

            {/* Add User Form */}
            {showAddUser && (
              <Card className="border-dashed">
                <CardContent className="pt-6">
                  <div className="flex items-end gap-4">
                    <div className="flex-1">
                      <Label htmlFor="user-select">Select User</Label>
                      <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                        <SelectTrigger id="user-select">
                          <SelectValue placeholder="Choose a user..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableUsers.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.display_name || `${user.first_name} ${user.last_name}`.trim() || user.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleAddUser} disabled={!selectedUserId || isAdding}>
                      {isAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                    </Button>
                    <Button variant="ghost" onClick={() => setShowAddUser(false)}>
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Users List */}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <div className="space-y-2">
                {filteredVisibility?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No users found
                  </div>
                ) : (
                  filteredVisibility?.map((visibility) => (
                    <Card key={visibility.id}>
                      <CardContent className="flex items-center justify-between py-4">
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {visibility.user?.display_name || 
                               `${visibility.user?.first_name} ${visibility.user?.last_name}`.trim() || 
                               'No name'}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {visibility.user?.email}
                            </span>
                          </div>
                          {!visibility.user?.active && (
                            <Badge variant="destructive">Inactive</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Label htmlFor={`visible-${visibility.id}`} className="text-sm">
                              Visible
                            </Label>
                            <Switch
                              id={`visible-${visibility.id}`}
                              checked={visibility.is_visible}
                              onCheckedChange={(checked) => 
                                toggleVisibility({ 
                                  userId: visibility.user_id, 
                                  dashboardType: selectedDashboard, 
                                  isVisible: checked 
                                })
                              }
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeUser({ userId: visibility.user_id, dashboardType: selectedDashboard })}
                            disabled={isRemoving}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};